/**
 * Capacity Inputs data layer (task 13.1, R3, R7.3, R7.7, R7.8).
 *
 * Wired to `/api/manpower/capacity-periods`, which serves Capacity_Period records
 * (headcount, workdays, OOF days, productive hours, availability factor) by FF
 * role, Region and period. Each scope may have multiple versions; the screen
 * surfaces the latest active version per scope and the mandatory recalculation
 * warning (R3.6) before a change persists.
 */
import {
  capacityPeriodListResponseSchema,
  capacityPeriodSchema,
  type CapacityPeriod,
  type CapacityPeriodEdit,
  type CapacityPeriodListResponse,
  type CapacityPeriodWrite,
} from '@complan/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch } from '@/shared/api/client';
import { manpowerQueryKeys } from '../manpower-query-keys';

/** Fetches every Capacity_Period record, unpaged (R9.3's volumetric baseline). */
export async function fetchCapacityPeriods(): Promise<CapacityPeriodListResponse> {
  const body = await apiFetch<unknown>('/api/manpower/capacity-periods');
  return capacityPeriodListResponseSchema.parse(body);
}

/** React Query hook for the Capacity_Period list (R3.1, R3.4). */
export function useCapacityPeriods(): UseQueryResult<CapacityPeriodListResponse, unknown> {
  return useQuery({
    queryKey: manpowerQueryKeys.capacityPeriods(),
    queryFn: fetchCapacityPeriods,
  });
}

/**
 * Creates a Capacity_Period version (R3.1, R3.2, R3.3, R3.6).
 *
 * Every submission is a new version for a scope (R3.2), and every submission
 * requires a mandatory change reason (R3.6) plus acknowledgement of the
 * recalculation warning (R3.6). A rule rejection (OOF days exceeding workdays)
 * comes back as `validation_failed` the caller surfaces from the thrown error.
 *
 * On success, invalidates all four Manpower screens (task 14.4), since a
 * Capacity_Period change can alter utilization and bottleneck classifications
 * on Capacity & Utilisation and Demand Explorer as well as the Capacity Inputs
 * screen itself.
 */
export function useCreateCapacityPeriod(): UseMutationResult<
  CapacityPeriod,
  unknown,
  CapacityPeriodWrite
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CapacityPeriodWrite) => {
      const body = await apiFetch<unknown>('/api/manpower/capacity-periods', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return capacityPeriodSchema.parse(body);
    },
    onSuccess: () => {
      // Invalidate all Manpower screens (task 14.4, R7.1, R7.2, R7.3, R7.4)
      void queryClient.invalidateQueries({ queryKey: manpowerQueryKeys.all });
    },
  });
}

/** What {@link useEditCapacityPeriod} submits: the record to edit, and its new figures. */
export interface CapacityPeriodEditRequest {
  /** The Capacity_Period version being edited. Its scope identifies the record (R3.1). */
  readonly id: string;
  readonly payload: CapacityPeriodEdit;
}

/**
 * Edits an existing Capacity_Period (R3.3, R3.6, R3.7).
 *
 * Adjusts the figures of a period that already exists — headcount, workdays, OOF
 * days, productive hours, availability factor — which the server records as the
 * next version of that period's scope, leaving the previous versions untouched
 * (R3.3). The payload carries no scope: role, Region and the effective period
 * identify the record being edited and come from `id`, so an edit cannot re-point
 * a period at a different scope and leave the original behind as a duplicate.
 * Recording a *different* scope is {@link useCreateCapacityPeriod}'s job.
 *
 * Two warnings can come back as `validation_failed` findings the caller
 * acknowledges and re-submits: R3.6's recalculation notice, and the capacity
 * shortfall raised when the new figures leave the period unable to cover the
 * demand already planned into it. Neither blocks — reducing capacity below
 * planned demand is a legitimate thing to record, and the period then reports as
 * over capacity on Capacity & Utilisation (R6.4, R6.5).
 *
 * On success, invalidates all four Manpower screens (task 14.4), since the
 * edited figures change utilization and bottleneck classifications as well as
 * this register.
 */
export function useEditCapacityPeriod(): UseMutationResult<
  CapacityPeriod,
  unknown,
  CapacityPeriodEditRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: CapacityPeriodEditRequest) => {
      const body = await apiFetch<unknown>(`/api/manpower/capacity-periods/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return capacityPeriodSchema.parse(body);
    },
    onSuccess: () => {
      // Invalidate all Manpower screens (task 14.4, R7.1, R7.2, R7.3, R7.4)
      void queryClient.invalidateQueries({ queryKey: manpowerQueryKeys.all });
    },
  });
}

/**
 * Deactivates a Capacity_Period version (R3.8). Deactivation is its own action
 * endpoint rather than a field on the edit call because an edit records new
 * figures for a period that stays in force, while deactivation withdraws the
 * period itself — and because Capacity_Period rows are never updated in place
 * (R3.3), so there is no edit call to carry `isActive: false` on.
 *
 * On success, invalidates all four Manpower screens (task 14.4).
 */
export function useDeactivateCapacityPeriod(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiFetch<unknown>(`/api/manpower/capacity-periods/${id}/deactivate`, {
        method: 'POST',
        body: JSON.stringify({ acknowledgedWarningRuleIds: [] }),
      });
    },
    onSuccess: () => {
      // Invalidate all Manpower screens (task 14.4, R7.1, R7.2, R7.3, R7.4)
      void queryClient.invalidateQueries({ queryKey: manpowerQueryKeys.all });
    },
  });
}
